import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { 
    Card, 
    CardContent, 
    CardHeader, 
    CardTitle, 
    CardDescription 
} from "../../components/ui/card";
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from "../../components/ui/table";
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
} from 'recharts';
import { 
    DollarSign, 
    TrendingUp, 
    UserCheck, 
    Download,
    Trophy,
    ArrowUpRight,
    Activity,
    ChevronLeft,
    ChevronRight
} from "lucide-react";
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { feesApi, FeeStatsResponse, FeeListResponse } from "../../api/fees.api";
import { toast } from "../../hooks/use-toast";
import { 
    Breadcrumb, 
    BreadcrumbItem, 
    BreadcrumbLink, 
    BreadcrumbList, 
    BreadcrumbPage, 
    BreadcrumbSeparator 
} from "../../components/ui/breadcrumb";

const PAGE_SIZE = 15;

export default function FeesPage() {
    useTranslation();
    const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month');
    const [stats, setStats] = useState<FeeStatsResponse | null>(null);
    const [feesList, setFeesList] = useState<FeeListResponse | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isTableLoading, setIsTableLoading] = useState(false);



    const fetchData = useCallback(async () => {
        setCurrentPage(1);
        try {
            const [statsData, listData] = await Promise.all([
                feesApi.getFeeStats(period),
                feesApi.listFees({ page: 1 })
            ]);
            setStats(statsData);
            setFeesList(listData);
        } catch (error) {
            console.error("Failed to fetch fee data", error);
            toast({
                title: "Erè",
                description: "Impossible de charger les données des frais.",
                type: "error"
            });
        }
    }, [period]);

    const fetchFeesList = useCallback(async (page: number) => {
        setIsTableLoading(true);
        try {
            const listData = await feesApi.listFees({ page });
            setFeesList(listData);
        } catch (error) {
            console.error("Failed to fetch fees page", error);
        } finally {
            setIsTableLoading(false);
        }
    }, []);

    // Fetch stats + first page of fees
    useEffect(() => {
        fetchData();
    }, [period, fetchData]);

    // Fetch fees when page changes
    useEffect(() => {
        fetchFeesList(currentPage);
    }, [currentPage, fetchFeesList]);

    const totalPages = feesList ? Math.ceil(feesList.count / PAGE_SIZE) : 1;

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const StatCard = ({ title, value, icon: Icon, description, color }: any) => (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
                <div className={`p-2 rounded-full ${color}`}>
                    <Icon className="h-4 w-4 text-white" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">{Number(value).toLocaleString()} HTG</div>
                <p className="text-xs text-muted-foreground mt-1">
                    {description}
                </p>
            </CardContent>
        </Card>
    );

    return (
        <div className="flex h-screen overflow-hidden bg-background">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-8 pt-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink>Administration</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>Gestion des Frais</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>

                        <div className="flex items-center space-x-2">
                            <select
                                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:ring-1 focus:ring-ring"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value as any)}
                            >
                                <option value="week">Semaine</option>
                                <option value="month">Mois</option>
                                <option value="year">Année</option>
                            </select>
                            <Button variant="outline" size="sm" onClick={fetchData}>
                                <Activity className="mr-2 h-4 w-4" />
                                Actualiser
                            </Button>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <StatCard 
                            title="Total Frais Collectés" 
                            value={stats?.total_fees || 0} 
                            icon={DollarSign} 
                            description="Cumul brut des frais"
                            color="bg-blue-500"
                        />
                        <StatCard 
                            title="Revenus Trésorerie" 
                            value={stats?.system_fees || 0} 
                            icon={Trophy} 
                            description="Part nette pour le système (70%)"
                            color="bg-indigo-600"
                        />
                        <StatCard 
                            title="Commissions Agents" 
                            value={stats?.agent_commissions || 0} 
                            icon={UserCheck} 
                            description="Part agents (30%)"
                            color="bg-emerald-500"
                        />
                        <StatCard 
                            title="Transactions avec Frais" 
                            value={stats?.transaction_count || 0} 
                            icon={ArrowUpRight} 
                            description="Nombre d'opérations"
                            color="bg-orange-500"
                        />
                    </div>

                    <div className="grid gap-4 md:grid-cols-7">
                        <Card className="col-span-4">
                            <CardHeader>
                                <CardTitle>Tendances des Revenus</CardTitle>
                                <CardDescription>Comparaison Système vs Agents sur la période</CardDescription>
                            </CardHeader>
                            <CardContent className="h-[350px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats?.trends || []}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                        <XAxis 
                                            dataKey="date" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 12 }} 
                                            dy={10}
                                        />
                                        <YAxis 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 12 }} 
                                            tickFormatter={(val) => `${val/1000}k`}
                                        />
                                        <Tooltip 
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend verticalAlign="top" height={36}/>
                                        <Line 
                                            type="monotone" 
                                            dataKey="system" 
                                            name="Système (HTG)" 
                                            stroke="#4f46e5" 
                                            strokeWidth={3} 
                                            dot={{ r: 4, fill: "#4f46e5" }}
                                            activeDot={{ r: 6 }}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="agent" 
                                            name="Agents (HTG)" 
                                            stroke="#10b981" 
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: "#10b981" }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        <Card className="col-span-3">
                            <CardHeader>
                                <CardTitle>Dernières Collectes</CardTitle>
                                <CardDescription>Historique en temps réel</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {(feesList?.results || []).slice(0, 6).map((fee) => (
                                        <div key={fee.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="bg-primary/10 p-2 rounded-md">
                                                    <TrendingUp className="h-4 w-4 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{fee.compte_code}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {new Date(fee.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-bold text-primary">+{fee.frais_total} HTG</p>
                                                <Badge variant="outline" className="text-[10px] py-0">
                                                    {fee.transaction_type}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Rapport Détaillé</CardTitle>
                                    <CardDescription>
                                        Liste exhaustive des frais appliqués
                                        {feesList && ` — ${feesList.count} enregistrement${feesList.count > 1 ? 's' : ''}`}
                                    </CardDescription>
                                </div>
                                <Button size="sm">
                                    <Download className="mr-2 h-4 w-4" /> Export CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="relative">
                                {isTableLoading && (
                                    <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-md">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                            Chargement...
                                        </div>
                                    </div>
                                )}
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Compte</TableHead>
                                            <TableHead>Type Tx</TableHead>
                                            <TableHead className="text-right">Frais Total</TableHead>
                                            <TableHead className="text-right">Part Système</TableHead>
                                            <TableHead className="text-right">Part Agent</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {feesList?.results.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell className="text-xs">
                                                    {new Date(row.created_at).toLocaleString()}
                                                </TableCell>
                                                <TableCell className="font-medium text-xs">{row.compte_code}</TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary" className="capitalize text-[10px]">
                                                        {row.transaction_type.replace('_', ' ')}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-semibold">{row.frais_total} HTG</TableCell>
                                                <TableCell className="text-right text-indigo-600 font-medium">{row.frais_system} HTG</TableCell>
                                                <TableCell className="text-right text-emerald-600 font-medium">{row.frais_agent} HTG</TableCell>
                                            </TableRow>
                                        ))}
                                        {(!feesList || feesList.results.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                                                    Aucune donnée trouvée pour cette période.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {feesList && totalPages > 1 && (
                                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                    <p className="text-sm text-muted-foreground">
                                        Page {currentPage} sur {totalPages} — {feesList.count} résultat{feesList.count > 1 ? 's' : ''}
                                    </p>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1 || isTableLoading}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        {getPageNumbers().map((page, idx) => (
                                            typeof page === 'number' ? (
                                                <Button
                                                    key={idx}
                                                    variant={page === currentPage ? "default" : "outline"}
                                                    size="sm"
                                                    className="w-9"
                                                    onClick={() => setCurrentPage(page)}
                                                    disabled={isTableLoading}
                                                >
                                                    {page}
                                                </Button>
                                            ) : (
                                                <span key={idx} className="px-1 text-muted-foreground">…</span>
                                            )
                                        ))}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages || isTableLoading}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
